// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Getting files in, and deciding what not to read.
 *
 * THE SKIP POLICY IS A FEATURE, NOT PLUMBING. A dropped repository contains `.git`, `node_modules`,
 * images, lockfiles and minified bundles. Getting this wrong means either a ninety-second hang or a
 * silently incomplete scan reported as clean — and the second is far worse. So every skipped file is
 * listed with its reason, and the count appears in the run summary. A file charsight did not read is
 * never allowed to look like a file charsight found nothing in.
 */

export type Route = 'text' | 'html' | 'pdf' | 'skipped';

export interface Incoming {
  name: string;
  bytes: Uint8Array;
  route: Route;
  skipReason?: string;
}

const BINARY_EXT = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'ico', 'tif', 'tiff', 'heic', 'psd',
  'mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac', 'mp4', 'mov', 'webm', 'avi', 'mkv',
  'zip', 'gz', 'tgz', 'bz2', 'xz', 'rar', '7z', 'tar', 'jar', 'war',
  'woff', 'woff2', 'ttf', 'otf', 'eot',
  'exe', 'dll', 'so', 'dylib', 'wasm', 'bin', 'o', 'a', 'class', 'pyc',
  'sqlite', 'db', 'parquet', 'onnx', 'gguf', 'safetensors', 'pt', 'pth', 'npz',
]);

const OFFICE_EXT = new Set(['docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt', 'odt', 'ods', 'odp']);

const NOISE_PATH = /(^|\/)(\.git|node_modules|\.next|dist|build|vendor|target|\.venv|__pycache__)\//;

const MAX_TEXT_BYTES = 8 * 1024 * 1024;
const MAX_PDF_BYTES = 32 * 1024 * 1024;
export const MAX_FILES = 800;

function extOf(name: string): string {
  const base = name.toLowerCase();
  const slash = base.lastIndexOf('/');
  const stem = slash >= 0 ? base.slice(slash + 1) : base;
  const dot = stem.lastIndexOf('.');
  return dot > 0 ? stem.slice(dot + 1) : '';
}

/**
 * Does this look like a binary file regardless of what it is called?
 *
 * A NUL byte in the first few kilobytes is the same heuristic `grep` and `git` use, and it catches
 * the extension-less binaries a repository is full of.
 */
export function looksBinary(bytes: Uint8Array): boolean {
  const n = Math.min(bytes.length, 8192);
  for (let i = 0; i < n; i++) if (bytes[i] === 0) return true;
  return false;
}

export function routeFor(name: string, bytes: Uint8Array): { route: Route; skipReason?: string } {
  const ext = extOf(name);

  if (NOISE_PATH.test(name)) {
    return { route: 'skipped', skipReason: 'inside a build, dependency or version-control directory' };
  }
  if (OFFICE_EXT.has(ext)) {
    return { route: 'skipped', skipReason: 'Office documents are out of scope for charsight' };
  }
  if (ext === 'pdf') {
    if (bytes.length > MAX_PDF_BYTES) {
      return { route: 'skipped', skipReason: `PDF larger than ${MAX_PDF_BYTES / 1048576} MB` };
    }
    return { route: 'pdf' };
  }
  if (BINARY_EXT.has(ext)) {
    return { route: 'skipped', skipReason: `a ${ext} file is not text` };
  }
  if (bytes.length > MAX_TEXT_BYTES) {
    return { route: 'skipped', skipReason: `larger than ${MAX_TEXT_BYTES / 1048576} MB` };
  }
  if (bytes.length === 0) {
    return { route: 'skipped', skipReason: 'empty' };
  }
  if (looksBinary(bytes)) {
    return { route: 'skipped', skipReason: 'contains null bytes, so it is not text' };
  }
  if (ext === 'html' || ext === 'htm' || ext === 'xhtml') return { route: 'html' };
  return { route: 'text' };
}

export async function readFiles(files: readonly File[]): Promise<Incoming[]> {
  const out: Incoming[] = [];
  for (const f of files.slice(0, MAX_FILES)) {
    const name = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    const bytes = new Uint8Array(await f.arrayBuffer());
    const { route, skipReason } = routeFor(name, bytes);
    out.push({ name, bytes, route, skipReason });
  }
  for (const f of files.slice(MAX_FILES)) {
    out.push({
      name: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
      bytes: new Uint8Array(0),
      route: 'skipped',
      skipReason: `over the ${MAX_FILES}-file limit for one run`,
    });
  }
  return out;
}

/**
 * Walk a dropped directory.
 *
 * `readEntries` returns at most 100 entries per call and gives no indication that it stopped, so the
 * naive single call silently truncates a large folder to its first hundred files — and a truncated
 * scan reported as clean is the failure this whole module exists to prevent.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  if (entries.length === 0) return Array.from(dt.files);

  const out: File[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (out.length >= MAX_FILES) return;
    if (entry.isFile) {
      const file = await new Promise<File | null>((resolve) => {
        (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
      });
      if (file) {
        Object.defineProperty(file, 'webkitRelativePath', {
          value: prefix ? `${prefix}/${file.name}` : file.name,
          configurable: true,
        });
        out.push(file);
      }
      return;
    }
    if (!entry.isDirectory) return;
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve) => {
        reader.readEntries(resolve, () => resolve([]));
      });
      if (batch.length === 0) break;
      for (const child of batch) {
        await walk(child, prefix ? `${prefix}/${entry.name}` : entry.name);
      }
    }
  };

  for (const e of entries) await walk(e, '');
  return out;
}

/** Can this browser hand a web page a folder at all? iOS Safari cannot, and says nothing about it. */
export function supportsFolderPicker(): boolean {
  return 'webkitdirectory' in document.createElement('input');
}

/**
 * Encode pasted text to bytes WITHOUT losing an unpaired surrogate.
 *
 * `TextEncoder` turns a lone surrogate into U+FFFD, so the character charsight would have reported
 * is destroyed before the scanner sees it — and it would then report a replacement character at a
 * position where the user never typed one. WTF-8 keeps it, and the decoder reports it as ill-formed,
 * which is the truth.
 */
export function encodePasted(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const u = text.charCodeAt(i);
    let cp = u;
    if (u >= 0xd800 && u <= 0xdbff && i + 1 < text.length) {
      const v = text.charCodeAt(i + 1);
      if (v >= 0xdc00 && v <= 0xdfff) {
        cp = 0x10000 + ((u - 0xd800) << 10) + (v - 0xdc00);
        i++;
      }
    }
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    } else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f),
      );
    }
  }
  return new Uint8Array(out);
}
